import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Users, Search, Briefcase, Building2, Download, UserPlus, Shield,
  Eye, Pencil, KeyRound, UserX, UserCheck, BarChart3, Headset, Phone as PhoneIcon, Calculator, Code2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { exportToCSV } from "@/lib/export-utils";
import AddSystemStaffDialog from "@/components/admin/staff/AddSystemStaffDialog";
import EditSystemStaffDialog from "@/components/admin/staff/EditSystemStaffDialog";
import SystemStaffDetailsDialog from "@/components/admin/staff/SystemStaffDetailsDialog";
import ResetPasswordSystemDialog from "@/components/admin/staff/ResetPasswordSystemDialog";

const COLORS = ["hsl(var(--primary))", "#22c55e", "#3b82f6", "#eab308", "#ef4444", "#8b5cf6"];

const roleLabels: Record<string, string> = {
  support: "دعم فني",
  call_center: "مركز اتصال",
  accountant: "محاسب",
  developer: "مطوّر",
  supervisor: "مشرف",
};

const roleIcons: Record<string, any> = {
  support: Headset,
  call_center: PhoneIcon,
  accountant: Calculator,
  developer: Code2,
  supervisor: Eye,
};

const departmentLabels: Record<string, string> = {
  general: "عام",
  technical: "تقني",
  financial: "مالي",
  operations: "عمليات",
  customer_service: "خدمة العملاء",
};

const statusConfig: Record<string, { label: string; color: string }> = {
  active: { label: "نشط", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
  inactive: { label: "معطّل", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" },
};

const AdminStaff = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editStaff, setEditStaff] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsStaff, setDetailsStaff] = useState<any>(null);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPwStaff, setResetPwStaff] = useState<any>(null);

  const { data: systemStaff, isLoading } = useQuery({
    queryKey: ["system-staff"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_staff")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const stats = useMemo(() => {
    if (!systemStaff) return { total: 0, active: 0, inactive: 0, byRole: {} as Record<string, number> };
    const byRole: Record<string, number> = {};
    systemStaff.forEach((s) => { byRole[s.role] = (byRole[s.role] || 0) + 1; });
    return {
      total: systemStaff.length,
      active: systemStaff.filter((s) => s.status === "active").length,
      inactive: systemStaff.filter((s) => s.status === "inactive").length,
      byRole,
    };
  }, [systemStaff]);

  const filtered = useMemo(() => {
    if (!systemStaff) return [];
    return systemStaff.filter((s) => {
      if (searchTerm && !s.full_name.includes(searchTerm) && !s.email.includes(searchTerm)) return false;
      if (filterRole !== "all" && s.role !== filterRole) return false;
      if (filterStatus !== "all" && s.status !== filterStatus) return false;
      return true;
    });
  }, [systemStaff, searchTerm, filterRole, filterStatus]);

  const roleDist = useMemo(() => {
    return Object.entries(stats.byRole).map(([role, count]) => ({
      name: roleLabels[role] || role, value: count,
    }));
  }, [stats.byRole]);

  const deptDist = useMemo(() => {
    if (!systemStaff) return [];
    const counts: Record<string, number> = {};
    systemStaff.forEach((s) => {
      const dept = departmentLabels[s.department || "general"] || s.department;
      counts[dept] = (counts[dept] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [systemStaff]);

  const handleToggleStatus = async (staff: any) => {
    const action = staff.status === "active" ? "deactivate" : "activate";
    const { data, error } = await supabase.functions.invoke("manage-system-staff", {
      body: { action, staff_id: staff.id },
    });
    if (error || data?.error) {
      toast.error(data?.error || error?.message || "خطأ");
      return;
    }
    toast.success(action === "deactivate" ? "تم تعطيل الحساب" : "تم تفعيل الحساب");
    queryClient.invalidateQueries({ queryKey: ["system-staff"] });
  };

  const handleExport = () => {
    if (!filtered.length) return;
    const rows = filtered.map((s) => ({
      "الاسم": s.full_name,
      "البريد": s.email,
      "الهاتف": s.phone || "",
      "الدور": roleLabels[s.role] || s.role,
      "القسم": departmentLabels[s.department || "general"] || s.department,
      "الحالة": statusConfig[s.status]?.label || s.status,
      "تاريخ الإضافة": format(new Date(s.created_at), "dd/MM/yyyy"),
    }));
    exportToCSV(rows, "system-staff-report");
    toast.success("تم تصدير التقرير");
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-500" />
            فريق المشرف العام
          </h1>
          <p className="text-muted-foreground text-sm">إدارة موظفي النظام: الدعم الفني، المحاسبين، المطورين، ومركز الاتصال</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!filtered.length}>
            <Download className="w-4 h-4 ml-1" />
            تصدير
          </Button>
          <Button className="gradient-hero border-0" size="sm" onClick={() => setAddOpen(true)}>
            <UserPlus className="w-4 h-4 ml-1" />
            إضافة موظف نظام
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10"><Users className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي الفريق</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-green-500/10"><UserCheck className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">نشطين</p>
              <p className="text-2xl font-bold">{stats.active}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-red-500/10"><UserX className="w-5 h-5 text-red-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">معطّلين</p>
              <p className="text-2xl font-bold">{stats.inactive}</p>
            </div>
          </CardContent>
        </Card>
        {Object.entries(stats.byRole).slice(0, 3).map(([role, count]) => {
          const Icon = roleIcons[role] || Briefcase;
          return (
            <Card key={role}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-blue-500/10"><Icon className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-xs text-muted-foreground">{roleLabels[role] || role}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="table" className="space-y-4">
        <TabsList>
          <TabsTrigger value="table"><Users className="w-4 h-4 ml-1" />الفريق</TabsTrigger>
          <TabsTrigger value="charts"><BarChart3 className="w-4 h-4 ml-1" />الرسوم البيانية</TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="بحث بالاسم أو البريد..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pr-9" />
                </div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-36"><SelectValue placeholder="الدور" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الأدوار</SelectItem>
                    {Object.entries(roleLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-28"><SelectValue placeholder="الحالة" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    <SelectItem value="active">نشط</SelectItem>
                    <SelectItem value="inactive">معطّل</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                فريق النظام ({filtered.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
              ) : (
                <TooltipProvider>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>الموظف</TableHead>
                        <TableHead>البريد الإلكتروني</TableHead>
                        <TableHead>الهاتف</TableHead>
                        <TableHead>الدور</TableHead>
                        <TableHead>القسم</TableHead>
                        <TableHead>الحالة</TableHead>
                        <TableHead>تاريخ الإضافة</TableHead>
                        <TableHead>إجراءات</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((s) => {
                        const RoleIcon = roleIcons[s.role] || Briefcase;
                        const sc = statusConfig[s.status] || statusConfig.active;
                        return (
                          <TableRow key={s.id} className={s.status === "inactive" ? "opacity-60" : ""}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <RoleIcon className="w-4 h-4 text-primary" />
                                </div>
                                {s.full_name}
                              </div>
                            </TableCell>
                            <TableCell dir="ltr" className="text-left text-sm">{s.email}</TableCell>
                            <TableCell dir="ltr" className="text-left text-sm">{s.phone || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-[10px]">
                                <RoleIcon className="w-3 h-3 ml-1" />
                                {roleLabels[s.role] || s.role}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">{departmentLabels[s.department || "general"] || s.department}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sc.color}`}>
                                {sc.label}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs">{format(new Date(s.created_at), "dd/MM/yyyy")}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-0.5">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setDetailsStaff(s); setDetailsOpen(true); }}>
                                      <Eye className="w-3.5 h-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>عرض التفاصيل</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditStaff(s); setEditOpen(true); }}>
                                      <Pencil className="w-3.5 h-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>تعديل</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setResetPwStaff(s); setResetPwOpen(true); }}>
                                      <KeyRound className="w-3.5 h-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>إعادة تعيين كلمة المرور</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost" size="icon"
                                      className={`h-7 w-7 ${s.status === "active" ? "text-destructive hover:text-destructive" : "text-green-600 hover:text-green-600"}`}
                                      onClick={() => handleToggleStatus(s)}
                                    >
                                      {s.status === "active" ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>{s.status === "active" ? "تعطيل" : "تفعيل"}</TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {!filtered.length && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            {systemStaff?.length ? "لا توجد نتائج للبحث" : "لم يتم إضافة أي موظف نظام بعد"}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TooltipProvider>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="charts" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">توزيع الأدوار</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  {roleDist.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={roleDist} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                          {roleDist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <ChartTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-center py-20 text-muted-foreground">لا توجد بيانات</p>}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">توزيع الأقسام</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  {deptDist.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={deptDist}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis />
                        <ChartTooltip />
                        <Bar dataKey="value" name="عدد" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-center py-20 text-muted-foreground">لا توجد بيانات</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AddSystemStaffDialog open={addOpen} onOpenChange={setAddOpen} />
      <EditSystemStaffDialog open={editOpen} onOpenChange={setEditOpen} staff={editStaff} />
      <SystemStaffDetailsDialog open={detailsOpen} onOpenChange={setDetailsOpen} staff={detailsStaff} />
      <ResetPasswordSystemDialog open={resetPwOpen} onOpenChange={setResetPwOpen} staff={resetPwStaff} />
    </div>
  );
};

export default AdminStaff;
